import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { useMutation } from 'react-query';
import { useUsEnv } from '../../utilities/sharedHooks/useUsEnv';
import { USER_LOGOUT } from '../UserSettings/mutations';
import { getCachedUserToken, resetUserSession } from '../../utilities/caching';
import { showAlert } from '../../utilities/notifications';
import OauthLayout from './partials/Layout';
import { useUserPersonalInfo } from '../../utilities/sharedHooks/user';
import { Typography } from '../../components';

const OauthAuthorizeComponent = () => {
  const { t } = useTranslation();
  const { oauthInteractionUid } = useParams();
  const formRef = useRef(null);
  const inputRef = useRef(null);
  const history = useHistory();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { isUsEnv } = useUsEnv();

  const { email } = useUserPersonalInfo();

  const token = getCachedUserToken();

  const baseUrl = isUsEnv
    ? process.env.REACT_APP_US_API_BASE_URL
    : process.env.REACT_APP_API_BASE_URL;

  const { mutate: logoutUser, isLoading: logoutLoading } = useMutation(USER_LOGOUT, {
    onSettled: (_, error) => {
      if (!error) {
        resetUserSession();
        history.push(
          `/oauth2/interaction/${oauthInteractionUid}?app=${
            isUsEnv ? process.env.REACT_APP_US_APP_NAME : process.env.REACT_APP_ROW_APP_NAME
          }`
        );
      } else {
        showAlert({ message: error.message, type: 'danger' });
      }
    },
  });

  const handleFormSubmit = (action) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    inputRef.current.value = action;
    formRef.current.submit();
  };

  const handleLogout = async () => {
    logoutUser();
  };

  return (
    <OauthLayout>
      <form
        ref={formRef}
        action={`${baseUrl}oauth2/interaction/${oauthInteractionUid}/confirm`}
        method="POST">
        <input type="hidden" ref={inputRef} name="action" />
        <input type="hidden" name="access_token" value={token} />
        <div className="d-flex flex-column gap-4">
          <div className="d-flex flex-column gap-2 align-items-center justify-content-center">
            <Typography as="h5" variant="largeBold">
              {t('oauth_consent_title')}
            </Typography>
            <Typography as="h6" variant="baseSemi">
              {t('oauth_consent_signed_in_as', { email })}
            </Typography>
            <div className="d-flex justify-content-center align-content-center text-center">
              <Typography as="h6" variant="base">
                {t('oauth_consent_subtitle')}
              </Typography>
            </div>
          </div>
          <div className="d-flex flex-column gap-2">
            <Typography as="p" variant="base">
              {t('oauth_consent_extra')}
            </Typography>
            <ul className="list-unstyled m-0 d-flex flex-column gap-2">
              <li>
                <Typography as="p" variant="base">
                  {t('oauth_consent_note1')}
                </Typography>
              </li>
              <li>
                <Typography as="p" variant="base">
                  {t('oauth_consent_note2')}
                </Typography>
              </li>
              <li>
                <Typography as="p" variant="base">
                  {t('oauth_consent_note3')}
                </Typography>
              </li>
            </ul>
          </div>
          <div className="d-flex flex-column gap-2 justify-content-center">
            <button
              type="button"
              loading={isSubmitting}
              disabled={logoutLoading || isSubmitting}
              onClick={() => {
                handleFormSubmit('confirm');
              }}
              className="btn btn-primary">
              {t('oauth_consent_approve')}
            </button>
            <div className="d-flex flex-column gap-1">
              <button
                className="btn text-primary py-1 text-btn-item"
                type="button"
                loading={isSubmitting}
                disabled={logoutLoading || isSubmitting}
                onClick={() => {
                  handleFormSubmit('deny');
                }}>
                {t('oauth_consent_deny')}
              </button>
              <button
                className="btn text-primary py-1 text-btn-item"
                type="button"
                loading={logoutLoading}
                disabled={logoutLoading || isSubmitting}
                onClick={() => {
                  handleLogout();
                }}>
                {t('oauth_consent_logout')}
              </button>
            </div>
          </div>
        </div>
      </form>
    </OauthLayout>
  );
};

export default OauthAuthorizeComponent;
